import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UserRole } from '../shared/models/user.model';

/**
 * Maps each protected route path to the roles allowed to access it.
 * Add new routes here as the application grows.
 */
export const ROUTE_ROLE_MAP: Record<string, UserRole[]> = {
    'generate-app': [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    dashboard: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DOMAIN_OPS],
    'user-management': [UserRole.SUPER_ADMIN],
    app: [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    'app-versions': [UserRole.SUPER_ADMIN, UserRole.ADMIN],
    'custom-domain-requests': [UserRole.SUPER_ADMIN, UserRole.DOMAIN_OPS],
};

@Injectable({ providedIn: 'root' })
export class RoleAccessGuard implements CanActivate {
    constructor(
        private readonly auth: Auth,
        private readonly router: Router
    ) {}

    public canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        const routePath = route.routeConfig?.path ?? '';
        const allowedRoles = ROUTE_ROLE_MAP[routePath] ?? [];

        return authState(this.auth).pipe(
            switchMap((user) => {
                if (!user) {
                    this.router.navigate(['/login']);
                    return of(false);
                }

                return from(user.getIdTokenResult()).pipe(
                    map((tokenResult) => {
                        const role = tokenResult.claims['role'] as string;

                        if (allowedRoles.includes(role as UserRole)) {
                            return true;
                        }

                        this.router.navigate(['/login']);
                        return false;
                    })
                );
            })
        );
    }
}
